import Logo from "../assets/logo.png";
import ThemeToggle from "./ThemeToggle";
import { useTheme } from "../context/ThemeContext";

// Admin — full nav
const ADMIN_NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      { key: "dashboard", label: "Dashboard" },
      { key: "activity", label: "Activity Log" },
    ],
  },
  {
    label: "Management",
    items: [
      { key: "affiliates", label: "Affiliates" },
      { key: "channels",   label: "Channels"   },
      { key: "devices",    label: "Devices"     },
    ],
  },
  {
    label: "Operations",
    items: [
      { key: "ota",  label: "OTA Updates" },
      { key: "logs", label: "Log Viewer"  },
      
    ],
  },
];

const SUPERUSER_NAV_SECTIONS = [
  ...ADMIN_NAV_SECTIONS,
  {
    label: "Administration",
    items: [
      { key: "users", label: "Users" },
    ],
  },
];

const PAGE_NAV_ITEM = {
  dashboard:  { key: "dashboard",  label: "Dashboard" },
  activity:   { key: "activity",   label: "Activity Log" },
  affiliates: { key: "affiliates", label: "Affiliates" },
  channels:   { key: "channels",   label: "Channels" },
  devices:    { key: "devices",    label: "Devices" },
  ota:        { key: "ota",        label: "OTA Updates" },
  logs:       { key: "logs",       label: "Log Viewer" },
};

// User — limited nav: dashboard, affiliates, devices
const USER_NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      { key: "dashboard", label: "Dashboard" },
      { key: "activity", label: "Activity Log" },
    ],
  },
  {
    label: "Management",
    items: [
      { key: "affiliates", label: "Affiliates" },
      { key: "devices",    label: "Devices"     },
    ],
  },
];

// Promo uploader — dashboard + channels only, no edit/status rights
const PROMO_NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      { key: "dashboard", label: "Dashboard" },
    ],
  },
  {
    label: "Management",
    items: [
      { key: "channels", label: "Channels" },
    ],
  },
];

const PAGE_LABELS = {
  dashboard:  "System Dashboard",
  activity:   "Activity Dashboard",
  affiliates: "Distributer Management",
  channels:   "Channel Management",
  devices:    "Device Management",
  ota:        "OTA Management",
  logs:       "Log Viewer",
  users:      "User Management",
  promos:     "Promo Management",
  profile:    "Profile & Settings",
};

export default function Shell({ active, setActive, children, user, onLogout }) {
  const { mode } = useTheme();
  const isAdmin     = user?.role === "admin";
  const isSuperuser = user?.role === "superuser";
  const isUser       = user?.role === "user";
  const isPromo       = user?.role === "promo";
  const isCustom       = user?.role === "custom";
  const hasSidebar = isAdmin || isSuperuser || isUser || isPromo || isCustom;

  const customNavSections = isCustom
    ? [{
        label: "Your Access",
        items: Object.entries(user?.permissions || {})
          .filter(([, level]) => level !== "none")
          .map(([key]) => PAGE_NAV_ITEM[key])
          .filter(Boolean),
      }]
    : [];

  const navSections = isSuperuser ? SUPERUSER_NAV_SECTIONS
    : isAdmin ? ADMIN_NAV_SECTIONS
    : isCustom ? customNavSections
    : isPromo ? PROMO_NAV_SECTIONS
    : USER_NAV_SECTIONS;

  return (
    <div style={{ display:"flex", height:"100vh", background:"var(--bg)", fontFamily:"'DM Sans', system-ui, sans-serif" }}>

      {hasSidebar && (
        <aside style={{ width:196, background:"var(--nav)", borderRight:`1px solid var(--border)`, display:"flex", flexDirection:"column", flexShrink:0 }}>
          <div style={{ padding:"18px 16px 14px", borderBottom:`1px solid var(--border)` }}>
            <div style={{ display:"flex", alignItems:"center", gap:9 }}>
              <div style={{ width:30, height:30,  borderRadius:7, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <img style={{ width:30, height:30 }} src={Logo} alt="logo" />
                <span style={{display:"flex", fontSize:12, color: "var(--text)", fontWeight:900}}>DRPL</span>
              </div>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:"var(--ink)", letterSpacing:"-0.01em" }}>DR Pulse360</div>
                <div style={{ background:"var(--surface-alt)", border:`1px solid var(--border)`, borderRadius:5, padding:"3px 9px", fontSize:10, fontWeight:700, color:"var(--text-sub)", letterSpacing:"0.07em", textTransform:"uppercase" }}>
                  {user?.role?.toUpperCase() || "ADMIN"}
                </div>
              </div>
            </div>
          </div>
          <nav style={{ flex:1, padding:"10px 8px", overflowY:"auto" }}>
            {navSections.map((sec) => (
              <div key={sec.label} style={{ marginBottom:2 }}>
                <div style={{ fontSize:10, fontWeight:700, color:"var(--text-dim)", letterSpacing:"0.08em", textTransform:"uppercase", padding:"10px 8px 4px" }}>
                  {sec.label}
                </div>
                {sec.items.map((item) => {
                  const isActive = active === item.key;
                  return (
                    <button
                      key={item.key}
                      onClick={() => setActive(item.key)}
                      style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"7px 9px", borderRadius:6, border:"none", background: isActive ? "var(--surface-alt)" : "transparent", color: isActive ? "var(--ink)" : "var(--text-sub)", cursor:"pointer", fontSize:12, fontWeight: isActive ? 600 : 400, textAlign:"left", transition:"all 0.12s", fontFamily:"inherit" }}
                    >
                      <span style={{ width:5, height:5, borderRadius:"50%", background: isActive ? "var(--ink)" : "var(--border-strong)", flexShrink:0 }} />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          <div style={{ padding:"8px", borderTop:"1px solid var(--border)", flexShrink:0 }}>
            <button
              onClick={() => setActive("profile")}
              style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"7px 9px", borderRadius:6, border:"none", background: active === "profile" ? "var(--surface-alt)" : "transparent", color: active === "profile" ? "var(--ink)" : "var(--text-sub)", cursor:"pointer", fontSize:12, fontWeight: active === "profile" ? 600 : 400, textAlign:"left", transition:"all 0.12s", fontFamily:"inherit" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
              Profile & Settings
            </button>
          </div>
        </aside>
      )}

      {/* Main content */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
        <header style={{ height:46, background:"var(--topbar)", borderBottom:`1px solid var(--border)`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 22px", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            {!hasSidebar && (
              <>
              <div style={{ width:30, height:30,  borderRadius:7, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <img style={{ width:30, height:30 }} src={Logo} alt="logo" />
                <span style={{display:"flex", fontSize:12, fontWeight:900}}>DRPL</span>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:13, fontWeight:700, color:"var(--ink)" }}>DR Pulse360</span>
                <div style={{ background:"var(--surface-alt)", border:`1px solid var(--border)`, borderRadius:5, padding:"3px 9px", fontSize:10, fontWeight:700, color:"var(--text-sub)", letterSpacing:"0.07em", textTransform:"uppercase" }}>
                  {user?.role?.toUpperCase() || "ADMIN"}
                </div>
                <div style={{ width:1, height:14, background:"var(--border)" }} />
              </div>
              </>
            )}
            <span style={{ fontSize:12, color:"var(--text-sub)", letterSpacing:"0.01em" }}>
              {hasSidebar ? (PAGE_LABELS[active] || active) : "My Dashboard"}
            </span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:10, color:"var(--text-dim)" }}>Auto-refresh: 5 min</span>
            <div style={{ width:1, height:14, background:"var(--border)" }} />
            <span style={{ fontSize:11, color:"var(--text-sub)" }}>{user?.name || user?.email}</span>
            <ThemeToggle />
            <button
              onClick={onLogout}
              style={{ background:"none", border:`1px solid var(--border)`, borderRadius:5, padding:"3px 9px", fontSize:11, color:"var(--text-sub)", cursor:"pointer", fontFamily:"inherit" }}
            >
              Sign out
            </button>
          </div>
        </header>
        <main style={{ flex:1, overflowY:"auto", padding:24 }}>
          {children}
        </main>
      </div>
    </div>
  );
}
